import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Pen, Monitor, Code, Cpu, Zap, Target, TrendingUp } from 'lucide-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const clientNames = ['STARTUPS', 'ENTERPRISES', 'AGENCIES', 'NON-PROFITS', 'ECOMMERCE', 'FINTECH', 'HEALTHCARE', 'REAL ESTATE']

const processSteps = [
  { icon: Pen, title: 'Discovery', desc: 'We dive deep into your business goals, target audience, and competitive landscape to build a strategy that works.' },
  { icon: Monitor, title: 'Design', desc: 'Our designers craft intuitive, beautiful interfaces and brand experiences that resonate with your users.' },
  { icon: Code, title: 'Development', desc: 'Engineers build your product using modern frameworks, clean code, and scalable architecture.' },
  { icon: Cpu, title: 'Launch & Scale', desc: 'We deploy, monitor, and optimize — ensuring your product performs and grows with your business.' },
]

const values = [
  { icon: Zap, title: 'Speed to Market', desc: 'Agile development with 2-week sprints. Get to market faster without sacrificing quality.' },
  { icon: Target, title: 'Outcome Focused', desc: 'We do not just ship code — we deliver measurable business results: revenue, conversions, growth.' },
  { icon: TrendingUp, title: 'Built to Scale', desc: 'Every product is architected for growth. No technical debt, no rebuilds, no regrets.' },
]

export default function About() {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.about-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 50, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', toggleActions: 'play none none none', invalidateOnRefresh: true }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <div className="min-h-screen bg-[#050505] pb-20" ref={sectionRef}>
      <Helmet>
        <title>About Us — Technifire Software</title>
        <meta name="description" content="Full-service digital agency based in Miami, FL. Founded 2020. 150+ projects delivered, 98% client satisfaction, 85% referral rate. We build digital products that work." />
        <link rel="canonical" href="https://technifire.io/about" />
        <meta property="og:title" content="About Us — Technifire Software" />
        <meta property="og:description" content="Full-service digital agency based in Miami, FL. 150+ projects delivered, 98% client satisfaction." />
        <meta property="og:url" content="https://technifire.io/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{`{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Technifire Software",
          "url": "https://technifire.io",
          "description": "Full-service digital agency based in Miami, Florida. 150+ projects delivered.",
          "foundingDate": "2020",
          "address": { "@type": "PostalAddress", "addressLocality": "Miami", "addressRegion": "FL", "addressCountry": "US" },
          "contactPoint": { "@type": "ContactPoint", "telephone": "+1-425-541-4001", "contactType": "sales" }
        }`}</script>
      </Helmet>
      {/* Hero */}
      <div className="px-6 lg:px-10 pt-24 pb-16">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 about-animate">
          <div className="max-w-3xl">
            <span className="text-mono text-[#9A9A9A] mb-3 block">ABOUT US</span>
            <h1 className="text-h2 uppercase text-white mb-6">
              STAY UNIQUE.<br />BE ON TOP.
            </h1>
            <p className="text-lg text-[#9A9A9A] leading-relaxed">
              We are TECHNIFIRE Software — a full-service digital agency based in Miami, Florida, 
              helping businesses build, launch, and scale through technology. From startups 
              pitching their first MVP to enterprises optimizing operations with AI, we partner 
              with ambitious teams who want digital products that actually work.
            </p>
          </div>
          <div className="flex gap-3">
            <div className="w-20 h-24 lg:w-24 lg:h-28 bg-gradient-to-br from-[#FF1493] to-[#FF6B9D] rounded-lg" />
            <div className="w-20 h-24 lg:w-24 lg:h-28 bg-gradient-to-br from-[#90EE90] to-[#2ECC71] rounded-lg" />
          </div>
        </div>
      </div>

      {/* Who we are */}
      <div className="px-6 lg:px-10 py-12 border-y border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 about-animate">
          <div>
            <h2 className="text-h3 text-white mb-4">Who We Are</h2>
            <p className="text-[#9A9A9A] leading-relaxed mb-4">
              Founded in 2020, TECHNIFIRE started with a simple belief: digital products should be 
              built with the same care and precision as physical ones. Today, we are a team of 
              designers, developers, and strategists who have delivered 150+ projects across 
              websites, SaaS platforms, e-commerce stores, CRM systems, and AI-powered solutions.
            </p>
            <p className="text-[#9A9A9A] leading-relaxed">
              We do not outsource. We do not cut corners. Every project gets a dedicated team 
              that treats your business like their own. That is why our clients stay — and why 
              85% of our business comes from referrals.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { value: '150+', label: 'Projects Delivered' },
              { value: '40+', label: 'SaaS Platforms Built' },
              { value: '$50M+', label: 'Revenue Generated for Clients' },
              { value: '98%', label: 'Client Satisfaction Rate' },
            ].map((stat) => (
              <div key={stat.label} className="bg-[#0a0a0a] rounded-xl p-5 border border-white/5">
                <span className="text-2xl lg:text-3xl font-light text-white">{stat.value}</span>
                <p className="text-mono text-[#9A9A9A] mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="px-6 lg:px-10 py-16">
        <span className="text-mono text-[#9A9A9A] mb-4 block about-animate">OUR VALUES</span>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {values.map((v) => (
            <div key={v.title} className="about-animate bg-[#0a0a0a] rounded-2xl p-6 border border-white/5">
              <v.icon className="w-8 h-8 text-white mb-4" />
              <h3 className="text-white font-medium mb-2">{v.title}</h3>
              <p className="text-sm text-[#9A9A9A]">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Industries marquee */}
      <div className="relative overflow-hidden py-6 border-y border-white/10 about-animate">
        <div className="flex animate-marquee gap-12 items-center">
          {[...clientNames, ...clientNames, ...clientNames, ...clientNames].map((name, i) => (
            <div key={i} className="flex-shrink-0 px-6 py-3 bg-white/5 rounded-full">
              <span className="text-sm font-medium text-white/40 tracking-widest">{name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Process section */}
      <div className="px-6 lg:px-10 py-16 bg-white text-black">
        <span className="text-mono text-gray-500 mb-4 block about-animate">HOW WE WORK</span>
        <h2 className="text-h3 uppercase mb-12 about-animate">
          SOLUTION IMPLEMENTATION<br />
          PROCESS. DEPLOYMENT WITHOUT CHAOS
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {processSteps.map((step, i) => (
            <div key={i} className="about-animate">
              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-[#111] flex items-center justify-center mb-4">
                <step.icon className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
              </div>
              <span className="text-mono text-gray-400 mb-1 block">STEP 0{i + 1}</span>
              <h4 className="text-lg font-medium mb-2">{step.title}</h4>
              <p className="text-sm text-gray-500">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 lg:px-10 py-16 about-animate">
        <div className="bg-gradient-to-r from-[#111] to-[#0a0a0a] rounded-2xl p-8 lg:p-12 border border-white/5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <h2 className="text-h3 text-white mb-2">Ready to build something great?</h2>
            <p className="text-[#9A9A9A]">Let's discuss your project and create a roadmap to success.</p>
          </div>
          <Link to="/contacts" className="btn-primary shrink-0">
            START A PROJECT
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  )
}
